/**
 * カスタムアクセント色（プリセット外の自由指定）。
 *
 * ---- なぜ「自由なカラーピッカー」ではないのか ----
 * profileAccent.ts の冒頭にあるとおり、アクセントは ProfilePage で MUI の
 * `palette.primary.main` に注入される。MUI は primary.main を**テキストにも使う**ので、
 * 地(#121212)に対して 4.5:1 を満たせない色を許すと、ユーザー自身の選択で
 * 自分のページの文字と罫線が読めなくなる。純青 #0000ff は 2.18:1 しかない。
 *
 * そこで**選べる座標系そのものを安全域に限定している。** ユーザーは
 * 色相・彩度・明度の3軸を動かすが、どこへ動かしても出力は下限を満たす。
 * 「選んだのに弾かれた」も「知らないうちに色が変わっていた」も起きない。
 * UI 側には制限している理由を必ず添えること（accentCustomNote）。
 *
 * ---- 明度スライダーが 0 でも黒にならない理由 ----
 * 0 は「その色相・彩度で**許される中で最も暗い色**」を指す。
 * 実際の下限は色相で変わる（青は暗く沈むので高い L が要る）ので、
 * スライダーの範囲を色相ごとに変えると、色相を動かすたびにつまみが飛ぶ。
 * 範囲を 0〜1 に固定し、下限側の実値を内部で色相ごとに解決している。
 *
 * ---- 保存値 ----
 * プリセットは key を保存する（色を後から調整できるようにするため）が、
 * カスタムは色そのものを保存するしかない。`personalize.d.acx` に hex で入る。
 * **サーバー側は personalize を素の JSONField で受けており検証が無い。**
 * したがって読み出し側で必ず parseCustomAccent を通すこと。
 * ここを素通しにすると、保存値がそのまま CSS の値として描画に流れる。
 */

// ---- OKLab / OKLCH <-> sRGB ----
// 式は scripts/genAccentPresets.js と同一。**片方だけ直さないこと**
// （プリセットの生成値とカスタムの生成値がズレると、同じ色相でも別の色になる）。
const gamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);
const linear = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

const oklchToRgb = (L: number, C: number, H: number): [number, number, number] => {
  const h = (H * Math.PI) / 180;
  const a = C * Math.cos(h);
  const b = C * Math.sin(h);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.2914855480 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
  ];
};

const rgbToOklch = (hex: string): { L: number; C: number; H: number } => {
  const n = parseInt(hex.slice(1), 16);
  const [R, G, B] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => linear(v / 255));
  const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B);
  const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B);
  const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B);
  const L = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s;
  const b = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s;
  return {
    L,
    C: Math.sqrt(a * a + b * b),
    H: ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360,
  };
};

const inGamut = (rgb: number[]) => rgb.every(v => v >= -1e-4 && v <= 1 + 1e-4);

/** その明度・色相で sRGB に収まる最大の彩度 */
const maxChroma = (L: number, H: number): number => {
  let lo = 0;
  let hi = 0.45;
  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    if (inGamut(oklchToRgb(L, mid, H))) lo = mid;
    else hi = mid;
  }
  return lo;
};

const toHex = (L: number, C: number, H: number): string =>
  `#${oklchToRgb(L, C, H)
    .map(v => Math.round(Math.min(1, Math.max(0, gamma(v))) * 255).toString(16).padStart(2, '0'))
    .join('')}`;

const relLuminance = (hex: string): number => {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => linear(v / 255));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** アプリの地。profileAccent.ts / 生成スクリプトと同じ値 */
const BG_LUMINANCE = relLuminance('#121212');

export const contrastOnAppBg = (hex: string): number =>
  (relLuminance(hex) + 0.05) / (BG_LUMINANCE + 0.05);

/**
 * 下限。**3:1 に緩めないこと。**
 * グラフィック要素の基準は 3:1 だが、この色は primary.main 経由でテキストにも乗る。
 * 緩めるなら先に primary.main を「明度補正した別の色」に差し替える必要がある
 * （profileAccent.ts の冒頭に経緯がある）。
 */
export const CONTRAST_FLOOR = 4.5;

/** 明度スライダー 0 / 1 に対応する希望明度。実際の下限は色相ごとに上へ寄る */
const L_AT_ZERO = 0.45;
const L_AT_ONE = 0.95;

export interface AccentAxes {
  /** 色相 0-360 */
  hue: number;
  /** 彩度 0-1。1 がその明度で出せる最大彩度 */
  chroma: number;
  /** 明度 0-1。0 は「許される中で最も暗い」であって黒ではない */
  light: number;
}

export const DEFAULT_AXES: AccentAxes = { hue: 120, chroma: 0.9, light: 0.5 };

/**
 * 3軸から hex を作る。**戻り値は必ず下限を満たす。**
 *
 * 希望明度で下限を割る場合は満たすまで明度を上げる（生成スクリプトの neonHex と同じ手）。
 * 彩度は明度に依存して上限が変わるので、明度を動かすたびに取り直す。
 */
export const axesToHex = ({ hue, chroma, light }: AccentAxes): string => {
  const h = ((hue % 360) + 360) % 360;
  const c01 = Math.min(1, Math.max(0, chroma));
  let L = L_AT_ZERO + Math.min(1, Math.max(0, light)) * (L_AT_ONE - L_AT_ZERO);
  let hex = toHex(L, c01 * maxChroma(L, h), h);
  while (contrastOnAppBg(hex) < CONTRAST_FLOOR && L < 0.99) {
    L += 0.005;
    hex = toHex(L, c01 * maxChroma(L, h), h);
  }
  return hex;
};

/** hex から3軸を復元する。パネルを開き直したときにつまみを戻すのに使う */
export const hexToAxes = (hex: string): AccentAxes => {
  const { L, C, H } = rgbToOklch(hex);
  const maxC = maxChroma(L, H);
  return {
    hue: H,
    chroma: maxC > 0 ? Math.min(1, C / maxC) : 0,
    light: Math.min(1, Math.max(0, (L - L_AT_ZERO) / (L_AT_ONE - L_AT_ZERO))),
  };
};

const HEX_RE = /^#[0-9a-f]{6}$/i;

/**
 * 保存値を検証する。**描画に使う前に必ず通すこと。**
 *
 * personalize はサーバー側で検証されていないので、ここが唯一の関門になる。
 * 形式が不正なら null を返し、呼び出し側は既定色へ落とすこと。
 * 形式が正しくても下限を割る値（旧い保存値や手で書き換えられた値）は、
 * 弾かずに明度を上げて返す。見た目は変わるが読めなくなるよりよい。
 */
export const parseCustomAccent = (value: unknown): string | null => {
  if (typeof value !== 'string' || !HEX_RE.test(value)) return null;
  const hex = value.toLowerCase();
  if (contrastOnAppBg(hex) >= CONTRAST_FLOOR) return hex;
  return axesToHex(hexToAxes(hex));
};

// ---- 色履歴 ----
// **端末ローカルに置く。** personalize に入れると公開プロフィールの応答にも乗り、
// 他人に自分の色の試行錯誤が見える上、全リクエストの重さになる。
// 履歴は利便のためのもので、端末を跨いで揃っている必要はない。
const HISTORY_KEY = 'profileAccentCustomHistory';
export const HISTORY_MAX = 8;

export const readAccentHistory = (): string[] => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const list: unknown = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    // 保存されている値も信用しない（他のタブや拡張から書き換えられる）
    return list
      .map(parseCustomAccent)
      .filter((v): v is string => v !== null)
      .slice(0, HISTORY_MAX);
  } catch {
    return [];
  }
};

/** 使った色を先頭へ。同じ色は重複させない */
export const pushAccentHistory = (hex: string): string[] => {
  const valid = parseCustomAccent(hex);
  if (!valid) return readAccentHistory();
  const next = [valid, ...readAccentHistory().filter(h => h !== valid)].slice(0, HISTORY_MAX);
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch {
    /* 覚えられないだけなので黙って諦める */
  }
  return next;
};
